import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Tooltip } from 'antd';
import { BalanceInformationNodeModel, EntryCodeNodeModel } from '../../models';
import {
    SectionInput,
    AccountingClassInput,
    HeadlineLossInput,
    EntryCodeValueInput,
    EntryCodeShareInput,
    EntryCodeSumInput
} from '../../node_inputs/EntryCodeNodeInputs';
import { InputRenderer } from '../../components';
import { ClickableIcon } from '../../../common/components';
import { WarningIcon } from '../../../custom_shared/icons';
import { CustomDivider } from '../../../custom_shared/components';
import { RemoveIcon } from '../../../custom_shared/icons';
import { StyleUtils } from '../../misc';
import { useAppStore } from '../../stores';
import './EntryCodeNodeRenderer.less';

interface Props {
    balanceInformationNode: BalanceInformationNodeModel;
    entryCodeNode: EntryCodeNodeModel;
}

const EntryCodeNodeRenderer: React.FC<Props> = ({ balanceInformationNode, entryCodeNode }) => {
    const { actionStore, sessionStore } = useAppStore();

    return (
        <>
            <div className="entry-code-node-renderer-container">
                <div className="entry-code-node-inputs-container">
                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={entryCodeNode.sectionInput} component={SectionInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer
                                input={entryCodeNode.accountingClassInput}
                                component={AccountingClassInput}
                            />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={entryCodeNode.headlineLossInput} component={HeadlineLossInput} />
                        </Col>
                    </Row>

                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={entryCodeNode.entryCodeValueInput} component={EntryCodeValueInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={entryCodeNode.entryCodeShareInput} component={EntryCodeShareInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={entryCodeNode.entryCodeSumInput} component={EntryCodeSumInput} />
                        </Col>
                    </Row>
                </div>

                <div className="entry-code-node-buttons-container">
                    {sessionStore.isReady && entryCodeNode.differenceMessage && (
                        <Tooltip
                            title={
                                <>
                                    <span>{entryCodeNode.differenceMessage.expectedLabel}</span>
                                    <br />
                                    <span>{entryCodeNode.differenceMessage.differenceLabel}</span>
                                </>
                            }
                            overlayStyle={{ maxWidth: 400 }}
                            overlayInnerStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                            <WarningIcon
                                className="entry-code-node-difference-message-icon"
                                color="#fa8c16"
                                width={20}
                                height={20}
                            />
                        </Tooltip>
                    )}

                    <ClickableIcon
                        icon={RemoveIcon}
                        width={18}
                        height={18}
                        disabled={balanceInformationNode.worksheet.isLocked}
                        onClick={() =>
                            actionStore.removeEntryCode(
                                balanceInformationNode.worksheetId,
                                balanceInformationNode.id,
                                entryCodeNode.id
                            )
                        }
                    />
                </div>
            </div>

            <CustomDivider type="horizontal" />
        </>
    );
};

export default observer(EntryCodeNodeRenderer);
